/**
 * createDiscreteGuard — the two-tap-arm guard (SD-5 / DEC-072) for composite
 * items whose sub-controls each deserve protection: button grids, and the
 * transpose / capo / waves selector rows.
 *
 * Each sub-control is identified by a stable string `key` (a cell id, a
 * semitone, a pitch class, a scale id - the component's choice). On a guarded
 * item, outside edit mode, the FIRST tap on a key arms it (a `GUARD_ARM_MS`
 * window, amber pulse via `data-armed`), and only the SECOND tap on the SAME
 * key fires. Tapping a different key re-arms that one instead. Auto-disarms
 * on timeout, on entering edit mode, and immediately after a fire.
 *
 * ALWAYS ON outside edit mode since 2026-09-25 (DEC-216, operator's choice).
 * It used to apply only in performance mode; once that became a background
 * switch any app can flip, a guard that depended on it would change what a
 * tap does on the iPad because of a press made on the Mac. Edit mode still
 * ignores it - test-firing there is deliberate (SD-5).
 *
 * Discrete mirror of Fader/Mixer's long-press-to-grab; shares the FireButton
 * arm semantics but factored out so grids/transpose/capo/waves don't each
 * reimplement the timer. FireButton keeps its own inline arm (it also owns
 * toggle/momentary/trigger firing) but reads the same `GUARD_ARM_MS`.
 *
 * Usage:
 *   const guard = createDiscreteGuard(() => !!props.item.guard);
 *   function onTap(key: string) {
 *     if (!guard.gate(key)) return;   // only armed — wait for the 2nd tap
 *     ...fire...
 *   }
 *   // JSX: data-armed={guard.armedKey() === key ? 'true' : 'false'}
 */

import { createSignal, createEffect, onCleanup } from 'solid-js';
import { isEditMode, GUARD_ARM_MS } from './store.ts';

/**
 * Is this item guarded? Absent `guard` means OFF for every kind EXCEPT
 * `sceneCopy`, where it means ON.
 *
 * Scene Copy's PASTE overwrites clips in the live Ableton set and there is
 * no undo path back through OSC, so it is the one control in the app that
 * is dangerous by default rather than dangerous by configuration.
 *
 * This lives here, and both the component and the Inspector's `GuardToggle`
 * call it, because a per-kind default written in two places is a checkbox
 * that eventually disagrees with the behaviour it claims to describe - the
 * toggle would read "off" while the control demanded two taps.
 */
export function guardEnabled(item: { kind: string; guard?: boolean }): boolean {
  return item.guard ?? item.kind === 'sceneCopy';
}

export interface DiscreteGuard {
  /** The currently-armed sub-control key, or null. Drives `data-armed`. */
  armedKey: () => string | null;
  /**
   * Call on each sub-control tap. Returns `true` when the tap should FIRE
   * (guard inactive, edit mode, OR this key was already armed), `false`
   * when the tap only ARMED (caller must not fire and should await the next).
   */
  gate: (key: string) => boolean;
  /** Force-disarm (e.g. on an external mode change). */
  disarm: () => void;
}

export function createDiscreteGuard(enabled: () => boolean): DiscreteGuard {
  const [armedKey, setArmedKey] = createSignal<string | null>(null);
  let timer: number | undefined;

  function disarm(): void {
    if (timer !== undefined) { clearTimeout(timer); timer = undefined; }
    setArmedKey(null);
  }
  function arm(key: string): void {
    setArmedKey(key);
    if (timer !== undefined) clearTimeout(timer);
    timer = window.setTimeout(() => { timer = undefined; setArmedKey(null); }, GUARD_ARM_MS);
  }
  onCleanup(disarm);
  // Entering edit mode clears any stale arm so leaving it starts safe.
  createEffect(() => { if (isEditMode()) disarm(); });

  function gate(key: string): boolean {
    if (isEditMode() || !enabled()) return true;       // unguarded: fire on 1st tap
    if (armedKey() === key) { disarm(); return true; } // 2nd tap on the armed key → fire
    arm(key);                                          // 1st tap → arm, don't fire
    return false;
  }

  return { armedKey, gate, disarm };
}
